<template>
  <div>
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h3">{{targetTableTitle}}</div>
      </div>
      <div class="vertical-center-box">
        <el-date-picker class="mr-r4" v-show="!(activeTab === 'userLoginHist')" style="width: 250px;" v-model="dateRange" type="daterange" @change="onChangeTable('datePicker')" align="right" unlink-panels
          :range-separator="defaultDateRangeOptions.rangeSeparator"
          :start-placeholder="defaultDateRangeOptions.startPlaceholder" :end-placeholder="defaultDateRangeOptions.endPlaceholder" :picker-options="defaultDateRangeOptions.pickerOptions"
          :default-time="defaultDateRangeOptions.defaultTime"></el-date-picker>
        <el-button class="mr-r4" size="mini" @click="clickExportExcel">
          <img src="@/assets/img/icons/excel.png" style="position:relative;left:-2px;bottom:2px;">
          <span style="left:2px;bottom:5px;">  엑셀출력</span>
        </el-button>
        <w-header-search-button size="mini" @click="setSearchToolVisible"></w-header-search-button>
      </div>
    </el-row>
    <el-table-wrapper :data="targetTableData" :columns="targetTableColumns" :pagination="true" :show-custom-header="true"
      :show-search-tool="showSearchTool" style="width:100%;" ref="userAccessTable"
      :column-default="columnDefault" @condition-change="onChangeTable" :total="total" remote
      v-loading="loading" :element-loading-text="loadingText">
    </el-table-wrapper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { columnDefault } from '@/constants/table';
import ACCESS_HIST from '@/constants/access-hist';
import { EMPTY_TEXT } from '@/utils/string';
import { defaultDateRangeOptions, getDateToString, getDuration } from '@/utils/date';
import msgUtil from '@/utils/message';
import FileApi from '@/api/common/file';
import UserAccessHistApi from '@/api/settings/user/user-access-hist';
import { isNil } from 'lodash';
import moment from 'moment';

export default {
  name: 'WUserAccessCommonTable',
  props: {
    activeTab: {
      type: String,
    },
    targetTable: {
      type: String,
    },
    targetTableData: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    targetTable: {
      immediate: true,
      handler(targetTable) {
        this.$nextTick(() => {
          this.targetTableColumns = [];
          if (targetTable === 'userLoginHist') {
            this.targetTableColumns = this.userLoginHistColumns;
            this.targetTableTitle = '접속 이력';
            // default dateRange: 최근 12시간(WDatetimeRangeButton)
          } else if (targetTable === 'menuAccessHist') {
            this.targetTableColumns = this.menuAccessHistColumns;
            this.targetTableTitle = '메뉴 접근 목록';
            // default dateRange: 금일
            this.dateRange = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
          } else if (targetTable === 'deviceActionHist') {
            this.targetTableColumns = this.equipPerformHistColumns;
            this.targetTableTitle = '장비 수행 목록';
            // default dateRange: 3개월
            this.dateRange = [moment().subtract(3, 'month').startOf('day').toDate(), moment().endOf('day').toDate()];
          } else if (targetTable === 'settingChangeHist') {
            this.targetTableColumns = this.settingChangeHistColumns;
            this.targetTableTitle = '설정 변경 목록';
            // default dateRange: 3개월
            this.dateRange = [moment().subtract(3, 'month').startOf('day').toDate(), moment().endOf('day').toDate()];
          }
          this.onChangeTable();
        });
      },
    },
  },
  computed: {
    ...mapGetters(['analysisDateParams']),
  },
  data() {
    const startDatetime = moment().subtract(3, 'month').startOf('day').toDate();
    const endDatetime = moment().endOf('day').toDate();
    return {
      columnDefault,
      defaultDateRangeOptions,
      dateRange: [startDatetime, endDatetime],
      // 로그인 - 로그아웃 이력
      userLoginHistColumns: [
        { prop: 'managerId', label: 'ID', searchable: true, sortable: true, remoteProp: 'm.manager_id' },
        { prop: 'managerName', label: '이름', searchable: true, sortable: true, remoteProp: 'm.manager_name' },
        { prop: 'connectIpaddress', label: '접속IP', width: 140, searchable: true, formatter: this.getEmptyText, sortable: true, remoteProp: 'mlh.connect_ipaddress' },
        { prop: 'managertypeName', label: '사용자구분', width: 100, searchable: true, sortable: true, remoteProp: 'bs.basicinfo_subname' },
        { prop: 'loginDatetime', label: '로그인시각', width: 140, searchType: 'date', searchable: true, formatter: this.getDateFormatter, sortable: true, remoteProp: 'mlh.login_datetime' },
        { prop: 'logoutDatetime', label: '로그아웃시각', width: 140, searchType: 'date', searchable: true, formatter: this.getDateFormatter, sortable: true, remoteProp: 'mlh.logout_datetime' },
        { prop: 'sessionConnectTime', label: '접속 유지 시간', width: 100, formatter: this.getSessionFormatter },
        {
          prop: 'resultFlag',
          label: '로그인 여부',
          remoteProp: 'mlh.result_flag',
          width: 100,
          formatter: this.getResultFlagFormatter,
          filters: [{ text: '성공', value: '1' }, { text: '실패', value: '0' }],
        },
        { prop: 'failLog', label: '로그인 실패 사유', formatter: this.getFailLogFormatter },
      ],
      // 메뉴 접근 이력
      menuAccessHistColumns: [
        { prop: 'managerId', label: 'ID', width: 300, searchable: true, sortable: true, remoteProp: 'm.manager_id' },
        { prop: 'managerName', label: '이름', searchable: true, width: 300, formatter: this.getEmptyText, sortable: true, remoteProp: 'm.manager_name' },
        { prop: 'connectIpaddress', label: '접속IP', searchable: true, sortable: true, formatter: this.getEmptyText, remoteProp: 'h.connect_ipaddress' },
        { prop: 'menuName', label: '메뉴명', searchable: true, sortable: true, formatter: this.getEmptyText, remoteProp: 'mi.menu_name' },
        { prop: 'checkDatetime', label: '접근시각', width: 150, searchType: 'date', searchable: true, formatter: this.getDateFormatter, sortable: true, remoteProp: 'h.check_datetime' },
      ],
      // 장비수행 이력
      equipPerformHistColumns: [
        { prop: 'managerId', label: 'ID', width: 140, searchable: true, sortable: true, remoteProp: 'm.manager_id' },
        { prop: 'managerName', label: '이름', width: 140, searchable: true, sortable: true, remoteProp: 'm.manager_name', formatter: this.getEmptyText },
        { prop: 'connectIpaddress', label: '접속IP', width: 120, searchable: true, sortable: true, remoteProp: 'mh.connect_ipaddress', formatter: this.getEmptyText },
        { prop: 'objName', label: '수행대상 장비', width: 250, searchable: true, sortable: true, remoteProp: 'o.obj_name', formatter: this.getEmptyText },
        { prop: 'actionContents', label: '수행내용', searchable: true, sortable: true, remoteProp: 'mh.action_contents', formatter: this.getEmptyText },
        { prop: 'checkDatetime', label: '수행일시', width: 130, searchType: 'date', searchable: true, formatter: this.getDateFormatter, sortable: true, remoteProp: 'mh.check_datetime' },
      ],
      // 설정변경 이력
      settingChangeHistColumns: [
        { prop: 'managerId', label: 'ID', width: 200, searchable: true, sortable: true, remoteProp: 'm.manager_id' },
        { prop: 'managerName', label: '이름', searchable: true, width: 200, formatter: this.getEmptyText, sortable: true, remoteProp: 'm.manager_name' },
        { prop: 'connectIpaddress', label: '접속IP', width: 150, searchable: true, sortable: true, formatter: this.getEmptyText, remoteProp: 'msh.connect_ipaddress' },
        {
          prop: 'changeType',
          label: '종류',
          width: 150,
          sortable: true,
          remoteProp: 'msh.change_type',
          filters: [{ text: '추가', value: '추가' }, { text: '수정', value: '수정' }, { text: '삭제', value: '삭제' }],
        },
        { prop: 'changeMessage', label: '수행내용', searchable: true, sortable: true, formatter: this.getEmptyText, remoteProp: 'msh.change_message' },
        { prop: 'checkDatetime', label: '수행일시', width: 150, searchType: 'date', searchable: true, formatter: this.getDateFormatter, sortable: true, remoteProp: 'msh.check_datetime' },
      ],
      showSearchTool: false,
      loading: false,
      loadingText: null,
      targetTableColumns: [],
      targetTableTitle: '',
    };
  },
  methods: {
    onChangeTable(type) {
      // 페이징 초기화 필요
      if (type === 'datePicker') {
        this.$refs.userAccessTable.clearFilter();
      }
      this.$emit('load-data', this.dateRange, this.$refs.userAccessTable.getParameter());
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    getEmptyText(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    setSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    clickExportExcel() {
      const tableParams = this.$refs.userAccessTable.getParameter();
      delete tableParams.start;
      delete tableParams.limit;
      const params = {
        ...tableParams,
        startDatetime: this.targetTable === 'userLoginHist' ? this.analysisDateParams.startDatetime : this.dateRange[0].getTime(),
        endDatetime: this.targetTable === 'userLoginHist' ? this.analysisDateParams.endDatetime : this.dateRange[1].getTime(),
      };
      this.loadingText = '엑셀 출력 중..';
      this.loading = true;
      let key = null;
      if (this.activeTab === 'userLoginHist') {
        key = 'user-access';
      } else if (this.activeTab === 'menuAccessHist') {
        key = 'menu-access';
      } else if (this.activeTab === 'deviceActionHist') {
        key = 'obj-action';
      } else if (this.activeTab === 'settingChangeHist') {
        key = 'setting-change';
      }
      UserAccessHistApi.exportHist(params, key).then((response) => {
        if (response.data.success && !isNil(response.data.data)) {
          FileApi.downloadExportedExcelFile(response.data.data);
        } else {
          this.$message(msgUtil.getResultMsgData('출력', '엑셀', false));
        }
      })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData('출력', '엑셀', false));
        })
        .finally(() => {
          this.loading = false;
          this.loadingText = null;
        });
    },
    // 경과시간
    getSessionFormatter(row) {
      if (!isNil(row.loginDatetime) && !isNil(row.logoutDatetime)) {
        return getDuration((row.logoutDatetime - row.loginDatetime));
      }
      return EMPTY_TEXT;
    },
    getFailLogFormatter(row) {
      if (!isNil(row.failLog)) {
        return ACCESS_HIST.loginFailLog[row.failLog];
      }

      return EMPTY_TEXT;
    },
    getResultFlagFormatter(row) {
      if (!isNil(row.resultFlag)) {
        return row.resultFlag === '0' ? '실패' : '성공';
      }

      return EMPTY_TEXT;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/basic/UserAndAuthManagement/UserAccessHist/components/UserAccessCommonTable.vue